import HyBase, { RequestOperation, type Action, type Options, type Actions } from './Base'
import type HyCollection from './Collection'
import type Field from './Field'
import { type Rule } from './Validation'

import {
  castArray,
  cloneDeep,
  defaults,
  defaultTo,
  each,
  every,
  filter,
  first,
  get,
  head,
  isString,
  isUndefined,
  keys,
  map,
  mapValues,
  merge,
  pick
} from 'lodash'

/**
 * Recursive deep copy helper that honours the "clone" function of models and
 * collections. This is required to support nested instances.
 *
 * @param {Object} source
 * @param {Object} target
 * @param {Array}  keys     Optional
 */
const copyFrom = function (
  source: Record<string, any>,
  target: Record<string, any>,
  keys?: string[]
): void {
  if (keys) {
    source = pick(source, keys)
  }
  each(source, (value, key): void => {
    if (isArray(value)) {
      // Copy arrays
      target[key] = []
      copyFrom(value, target[key])
    } else if (isPlainObject(value)) {
      // Copy plain objects recursively
      target[key] = {}
      copyFrom(value, target[key])
    } else if (
      isObject(value) &&
      isFunction((value as HyModel).clone)
      // TODO: Allow for Collections
      // isFunction((value as HyCollection | HyModel).clone)
    ) {
      // Clone embedded Models and Collections using their clone() method
      target[key] = (value as HyModel).clone()
      // TODO: Allow for Collections
      // Vue.set(target, key, (value as HyCollection | HyModel).clone())
    } else {
      // Clone other values using cloneDeep()
      target[key] = cloneDeep(value)
    }
  })
}

class HyModel extends HyBase {
  // Attribute access
  [key: string]: any
  // Attribute storage
  private readonly _values!: Record<string, any>
  private readonly _reference!: Record<string, any>
  private readonly _errors!: Record<string, string[]>
  private readonly _collections!: Record<string, HyCollection>

  /**
   * A convenience wrapper around the model's attributes that are saved.
   * This is similar to the `saved` method, but instead of accessing a single
   * property it returns the whole saved object, so that you can do something
   * like model.$.attribute when you want to display it somewhere.
   *
   * @returns {Object} This model's saved, reference data.
   */
  get $(): Record<string, any> {
    return this._reference
  }

  /**
   * Boolean flag to indicate if this model is currently in an active state.
   *
   * @returns {Boolean} This model's active state.
   */
  get active(): Boolean {
    return this.loading || this.saving || this.deleting
  }

  /**
   * @returns {Object} This model's "active" state attributes.
   */
  get attributes(): Record<string, any> {
    return this._values
  }

  /**
   * Creates a new instance, called when using 'new'.
   *
   * @param  {Object}     [attributes]  HyModel attributes
   * @param  {HyCollection} [collection]  HyCollection that this model belongs to.
   * @param  {Object}     [options]     Options to set on the model.
   */
  constructor(
    attributes = {},
    collection: HyCollection | null = null,
    options = {}
  ) {
    super(options)
    // @ts-ignore
    this._collections = reactive({}) // Collections that contain this model.
    this._reference = reactive({}) // Saved attribute state.
    this._values = reactive({}) // Active attribute state.
    this._errors = reactive({}) // Active attribute state.
    // Vue.set(this, '_mutations',   {});  // Mutator cache.
    // Reset flags
    this.clear()
    // Cache certain methods that don't need to be evaluated more than once.
    // this.memoize();
    // Cache mutator pipelines so that they can run as a single function.
    // this.compileMutators();
    // Set default values
    // each(castArray(this.fields()), (field: Field) => this._values[field.name] = field.default)
    // Assign all defaults and given model data to the model's attributes and reference.
    this.assign(defaults(attributes, this.defaults()))
    // Register the given collection (if any) to the model. This is so that
    // the model can be added to the collection automatically when it is
    // created on save, or removed on delete.
    if (collection) {
      this.registerCollection(collection)
    }
  }

  // Options

  /**
   * @returns The default options for this model.
   */
  override getDefaultOptions(): ModelOptions {
    return merge({}, super.getDefaultOptions(), {
      // The attribute that should be used to uniquely identify this model.
      identifier: 'id',

      // Whether this model should allow an existing identifier to be
      // overwritten on update.
      // overwriteIdentifier: false,

      // Whether this model should perform a "patch" on update,
      // which will only send changed attributes in the request.
      // TODO: Check if changing to false is better
      patch: true,

      // Whether this model should save even if no attributes have changed
      // since the last time they were synced. If set to `false` and no
      // changes have been made, the request will be considered a success.
      saveUnchanged: false,

      // Whether this model should only use the first validation error it
      // receives, rather than an array of errors.
      // useFirstErrorOnly: false,

      // Whether this model should validate an attribute that has changed.
      // This would only affect the errors of the changed attribute and
      // will only be applied if the value is not a blank string.
      // validateOnChange: false,

      // Whether this model should validate models and collections within
      // its attribute tree. The result is implicit recursion as each of
      // those instances will also validate their trees, etc.
      // validateRecursively: true,

      // Whether this model should mutate a property as it is changed,
      // before it is set. This is a rare requirement because you usually
      // don't  want to mutate something that you are busy editing.
      // mutateOnChange: false,

      // Whether this model should mutate all attributes before they are
      // synced to the "saved" state. This would include construction,
      // on fetch, on save, and on assign.
      // mutateBeforeSync: true,

      // Whether this model should use mutated values for the attributes
      // in "save" request. This will not mutate the active state.
      // mutateBeforeSave: true,
    })
  }

  /**
   * Sync the current attributes to the reference attributes. This is usually
   * only called on save. We have to clone the values otherwise we
   * end up with references to the same object in both attribute sets.
   *
   * You can also pass one or an array of attributes to sync.
   *
   * @param attribute
   */
  sync(attribute?: string | string[]): void {
    // Mutate all attributes before we sync them, if required to do so.
    // if (this.getOption('mutateBeforeSync')) {
    //     this.mutate(attribute);
    // }
    // We're cloning deep to avoid multiple references to the same object,
    // otherwise updating the attributes will also update the reference.
    // Set each saved attribute to its active equivalent.
    const active: Record<string, any> = cloneDeep(this._values)
    // Use all attributes if none are specified
    if (isUndefined(attribute)) {
      attribute = keys(this._values)
    }
    // Copy current attributes to reference
    each(castArray(attribute), (attribute): void => {
      this._reference[attribute] = get(active, attribute)
    })
    // this.emit('sync');
  }

  /**
   * @returns {boolean} whether this model is not persisted yet, ie. has not
   *                    been created yet. The default test is to check if the
   *                    model's identifier is missing.
   */
  isNew(): boolean {
    return !this.identifier()
  }

  /**
   * @returns {boolean} the opposite of `isNew`, returns `true` if this model
   *                    is already persisted somewhere else.
   */
  isExisting(): boolean {
    return !this.isNew()
  }

  /**
   * Registers an attribute on this model so that it can be accessed directly
   * on the model, passing through `get` and `set`.
   * 
   * @param attribute The name of the attribute
   */
  // registerAttribute(attribute: string): void {
  //   // Protect against unwillingly using an attribute name that already
  //   // exists as an internal property or method name.
  //   if (has(HyBase.RESERVED, attribute)) {
  //     throw new Error(`Can't use reserved attribute name '${attribute}'`)
  //   }
  //   // Create dynamic accessors and mutations so that we can update the
  //   // model directly while also keeping the model attributes in sync.
  //   Object.defineProperty(this, attribute, {
  //     get: (): any => this.get(attribute),
  //     set: <T>(value: T): T | undefined => this.set(attribute, value)
  //   })
  // }

  /**
   * Returns the model's identifier value.
   */
  identifier(): string {
    return this.saved(this.getOption('identifier'))
  }

  /**
   * @returns An empty representation of this model.
   *          It's important that all model attributes have a default
   *          value in order to be reactive in Vue.
   */
  defaults(): Object {
    return this.fields().reduce((defaults: Record<string, any>, field: Field) => {
      // Add default value if present, check for boolean because
      // isEmpty() will return true for boolean values
      if (isBoolean(field.default) || !isEmpty(field.default)) { defaults[field.name] = field.default }
      return defaults
    }, {})
  }

  /**
   * By default, 'fetch', 'create', 'update', and 'delete' will
   * be created for a given model according to standard REST principles.
   *
   * @returns {Object<string, Action>}
   */
  getDefaultActions(): Actions {
    return {
      fetch: { method: 'GET', route: '{_remote_path}/{_identifier}' },
      create: { method: 'POST', route: '{_remote_path}/' },
      update: { method: 'PUT', route: '{_remote_path}/{_identifier}' },
      delete: { method: 'DELETE', route: '{_remote_path}/{_identifier}' }
    }
  }

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {Object<string, Action>} The corresponding action details
   */
  override getAction(action: string): Action {
    if (action === 'save') {
      action = this.isNew() ? 'create' : 'update'
    }
    return this.getOption(`actions.${action}`)
  }

  /**
   * @returns {Object} Parameters to use for replacement in route patterns.
   */
  override getRouteParameters(): Record<string, any> {
    const model = {
      _identifier: this.identifier(),
      _remote_path: this.getOption('remotePath') || `${useSnakeCase(this.getOption('remoteModel'))}s`
    }
    return merge(model, super.getRouteParameters(), this._values)
  }

  /**
   * Assigns all given model data to the model's attributes and reference.
   * This will also fill any gaps using the model's default attributes.
   *
   * @param {Object} attributes
   *
   * @returns {Object} The attributes that were assigned to the model.
   */
  assign(attributes: Record<string, any>): void {
    this.set(defaults({}, attributes))
    this.sync()
  }

  display(attribute: string): any {
    return this.field(attribute).display
  }

  /**
   * Returns true if the model has values matching the where clause
   * 
   * @param {Object} where The where clause to match against.
   * 
   * @returns {Boolean} Whether the object matches the where clause
   */
  matches(where: Record<string, string>): boolean {
    return every(where, (value, key): boolean => this.get(key) === value)
  }

  // TODO: better getSaveData Docs
  // Receives a formData object and fills it with the form
  // representation of the model's data. Only includes changed
  // fields.
  getSaveData(formData: FormData) {
    const saveFields = this.getOption('patch') ? this.changed() : this.fieldNames()
    // @ts-ignore
    // Since this.changed() can return false, this doesn't match the typings of each(),
    // but it won't affect the actual result. Also, skip virtual fields because we are
    // processing them separately no matter the patch setting.
    each(saveFields, (field) => {
      if (this.field(field).type !== 'virtual') {
        this.field(field).addFormData(formData)
      }
    })
    // Add virtual fields
    each(this.fields(), (field: any) => {
      if (field.type === 'virtual') {
        field.addFormData(formData)
      }
    })
  }

  /**
   * Sets the value of an attribute and registers the magic "getter" in a way
   * that is compatible with Vue's reactivity. This method should always be
   * used when setting the value of an attribute.
   *
   * @param attribute
   * @param value
   *
   * @returns {*} The value that was set.
   */
  set<T = any>(
    attribute: string | Record<string, any>,
    value?: T
  ): T | undefined {
    // Allow batch set of multiple attributes at once, ie. set({...});
    if (isPlainObject(attribute)) {
      each(attribute as Record<string, any>, (value, key): void => {
        this.set(key, value)
      })
      return
    }
    // Sanity check -- we should only be setting properties we have
    // defined. Issue a warning if this is not the case
    // Only register the pass-through property if it's not already set up.
    // If it already exists on the instance, we know it has been.
    const defined: boolean = this.hasField(attribute as string)
    if (!defined) {
      // console.error(`${attribute} cannot be set because it's not a defined field`)
      return value
    }
    const field = this.field(attribute as string)
    // Current value of the attribute, or `undefined` if not set
    // let previous: any = this.get(attribute as string)
    // Run the attribute's mutations if required to do so on change.
    // if (this.getOption('mutateOnChange')) {
    //     value = this.mutated(attribute as string, value);
    // }
    // Set value
    if (field.type === 'collection') {
      // Set collection
      this._values[attribute as string].add(value)
    } else {
      // Plain JS value
      this._values[attribute as string] = value
    }
    // Only consider a change if the attribute was already defined.
    // let changed: boolean = defined && !isEqual(previous, value);
    // if (changed) {
    //     // Validate on change only if it's not the first time it's set.
    //     if (this.getOption('validateOnChange')) {
    //         Vue.nextTick((): Promise<ValidationResultErrorFinalResult> => this.validateAttribute(attribute as string));
    //     }
    //     // Emit the change event after
    //     this.emit('change', {attribute, previous, value});
    // }
    return value
  }

  /**
   * Reverts all attributes back to their defaults, or `undefined` if a
   * default value is not defined.
   *
   * You can also pass one or an array of attributes to unset.
   *
   * @param {string|string[]} attribute
   */
  unset(attribute: string | string[]): void {
    // We're cloning deep to avoid multiple references to the same object,
    // otherwise updating the attributes will also update the reference.
    const defaults: Record<string, any> = cloneDeep(this.defaults())
    // Unset either specific attributes or all attributes if none provided.
    const attributes: string | string[] = defaultTo(
      attribute,
      keys(this._values)
    )
    // Unset either specific attributes or all attributes if none provided.
    each(castArray(attributes), (attribute): void => {
      if (this.has(attribute)) {
        this._values[attribute] = get(defaults, attribute)
      }
    })
  }

  /**
   * Similar to `saved`, returns an attribute's value or a fallback value
   * if this model doesn't have the attribute.
   *
   * @param {string} attribute
   * @param {*}      fallback
   *
   * @returns {*} The value of the attribute or `fallback` if not found.
   */
  get(attribute: string, fallback?: any): any {
    return get(this._values, attribute, fallback)
  }

  /**
   * Resets all attributes back to their reference values (source of truth).
   * A good use case for this is when form fields are bound directly to the
   * model's attributes. Changing values in the form fields will change the
   * attributes on the model. On cancel, you can revert the model back to
   * its saved, original state using reset().
   *
   * You can also pass one or an array of attributes to reset.
   *
   * @param {string|string[]} attribute
   */
  reset(attribute: string | string[]): void {
    // Reset specific attributes.
    if (attribute) {
      copyFrom(this._reference, this._values, castArray(attribute))

      // Reset all attributes if one or more specific ones were not given.
    } else {
      copyFrom(this._reference, this._values)
    }
    this.clearErrors()
    // this.emit('reset')
  }
  /**
   * Similar to `get`, but accesses the saved attributes instead.
   *
   * This is useful in cases where you want to display an attribute but also
   * change it. For example, a modal with a title based on a model field, but
   * you're also editing that field. The title will be updating reactively if
   * it's bound to the active attribute, so bind to the saved one instead.
   *
   * @param {string} attribute
   * @param {*}      fallback
   *
   * @returns {*} The value of the attribute or `fallback` if not found.
   */
  saved(attribute: string, fallback?: any): any {
    return get(this._reference, attribute, fallback)
  }

  /**
   * Returns an array of attribute names that have changed, or `false` if no
   * changes have been made since the last time this model was synced.
   *
   * @returns {Array|boolean} An array of changed attribute names, or `false`
   *                         if no attributes have changed since the last sync.
   */
  changed(): string[] | false {
    const changed: string[] = []
    // Check for changed values
    each(this._values, (value, attribute): void => {
      if (!isEqual(value, this.saved(attribute))) {
        changed.push(attribute)
      }
    })
    // Normalize empty arrays to false
    return !isEmpty(changed) ? changed : false
  }

  // TODO: unchanged() documentation
  get unchanged(): boolean {
    return !this.changed()
  }

  /**
   * Determines if the model has an attribute.
   *
   * @param  {string}  attribute
   * @returns {boolean} `true` if an attribute exists, `false` otherwise.
   *                    Will return true if the object exists but is undefined.
   */
  // has(attribute: string): boolean {
  //   return has(this._values, attribute)
  // }

  // Validation

  /**
   * Return array of rules from fields
   */
  validation(): Record<string, Rule> {
    const properties: Record<string, any> = {}
    map(this.fields(), (field: Field) => {
      properties[field.name] = field.validation
    })
    return properties
  }

  /**
   * @return {Array}
   */
  getValidateRules(attribute: string): Rule[] {
    return castArray(this.validation()[attribute] || [] as Rule[])
  }

  /**
   * Validates a specific attribute of this model, and sets errors for it.
   *
   * @returns {boolean} `true` if valid, `false` otherwise.
   */
  validateAttribute(
    attribute: string
  ): Promise<ValidationResultErrorFinalResult> {
    // Only validate fields we know about
    if (!this.hasField(attribute)) {
      return Promise.reject(new Error(`'${attribute}' is not defined`))
    }
    // Set up list of validation rules/tasks
    const value: any = this.get(attribute)
    const rules: Rule[] = this.getValidateRules(attribute)
    let tasks: ValidationTask[] = rules.map((rule): true | string => {
      return rule(value, attribute, this)
    })
    // Check if any nested values should be validated also.
    // if (this.getOption('validateRecursively')) {
    //   if (isFunction(get(value, 'validate'))) {
    //     tasks.push((value as HyModel).validate())
    //   }
    // }
    // Return a promise that resolves when all validations are complete
    return (Promise.all(tasks) as Promise<ValidationResultError[]>).then(
      (errors: ValidationResultError[]): ValidationResultErrorFinalResult => {
        // Unpack a nested error set.
        if (isArray(errors) && isArray(first(errors))) {
          errors = first(
            errors as ValidationResult[]
          ) as ValidationResultError[]
        }
        // Errors will always be messages or nested error objects.
        errors = filter(
          errors,
          (e): boolean => isString(e) || isObject(e)
        ) as ValidationResultError[]
        // Set errors for the model being validated.
        this.setAttributeErrors(attribute, errors)
        // Check to see if we should yield only the first error.
        if (this.getOption('useFirstErrorOnly') && !isEmpty(errors)) {
          return first(errors)!
        }
        // Return errors
        return errors
      }
    )
  }

  /**
   * Validates all attributes.
   *
   * @param {Object} [attributes] One or more attributes to validate.
   *
   * @returns {Promise}
   */
  validate(
    attributes?: string | string[]
  ): Promise<ValidationResultErrorFinalResult> {
    // No attributes means all attributes
    if (isUndefined(attributes)) {
      attributes = this.fieldNames()
    }
    // Support a single, string attribute.
    if (isString(attributes)) {
      return this.validateAttribute(attributes).then(
        (errors): Record<string, string> => {
          return (
            !isEmpty(errors) ? { [attributes as string]: errors } : {}
          ) as Record<string, string>
        }
      )
    }
    // Support an array of attributes to validate.
    if (isArray(attributes)) {
      let $errors: Record<string, ValidationResultErrorFinalResult> = {}
      let tasks: Promise<void>[] = attributes.map(
        (attribute): Promise<void> => {
          return this.validateAttribute(attribute).then(
            (errors: ValidationResultErrorFinalResult): void => {
              if (!isEmpty(errors)) {
                $errors[attribute] = errors
              }
            }
          )
        }
      )
      return Promise.all(tasks).then(
        (): Record<string, ValidationResultErrorFinalResult> => $errors
      )
    }
    // Not the expected input
    return Promise.reject(
      new Error('Invalid argument for validation attributes')
    )
  }

  /**
   * @returns {Object} This model's errors, which are cleared automatically.
   */
  get errors(): Record<string, string | string[]> {
    return this.getErrors()
  }

  get humanReadableErrors(): string[] {
    return this.getHumanReadableErrors()
  }

  /**
   * Sets errors for a specific attribute. Support the ability to clear error
   * by passing an empty value.
   *
   * @param {string}       attribute
   * @param {string|array} errors
   */
  setAttributeErrors(
    attribute: string,
    errors?: string | string[] | ValidationResultError[]
  ): void {
    if (isEmpty(errors)) {
      delete this._errors[attribute]
    } else {
      this._errors[attribute] = castArray(errors) as string[]
    }
  }

  /**
   * Sets the errors on this model.
   *
   * @param {Object} errors
   */
  setErrors(errors?: Record<string, string | string[]>): void {
    // If we have no errors, clear existing errors
    if (isEmpty(errors)) {
      each(this._errors, (errors, attribute): void => {
        this.setAttributeErrors(attribute, '')
      })
      return
    }
    // If we have errors, add them to the error stack
    each(errors, (errors, attribute): void => {
      this.setAttributeErrors(attribute, errors)
    })
  }

  /**
   * @returns {Object} Validation errors on this model.
   */
  getErrors(): Record<string, string | string[]> {
    // TODO: Do we want to do this?
    if (this.getOption('useFirstErrorOnly')) {
      return mapValues(this._errors, head) as Record<string, string>
    }
    return this._errors
  }

  getHumanReadableErrors(): string[] {
    const humanReadableErrors: string[] = []
    each(this._errors, (errors, attribute): void => {
      errors.forEach((error): void => {
        const humanFieldName = this.field(attribute).label
        humanReadableErrors.push(`${humanFieldName} ${error}`)
      })
    })
    return humanReadableErrors
  }

  /**
   * Clears all errors on this model.
   */
  clearErrors(): void {
    this.setErrors({})
    this.setFlag('fatal', false)
  }

  /**
   * Reverts all attributes back to their defaults, and completely removes all
   * attributes that don't have defaults. This will also sync the reference
   * attributes, and is not reversable.
   */
  clearAttributes(): void {
    const defaults: Record<string, any> = this.defaults()
    this._values.value = cloneDeep(defaults)
    this._reference.value = cloneDeep(defaults)
  }

  /**
   * Reverts all attributes back to their defaults, and completely removes all
   * attributes that don't have defaults. This will also sync the reference
   * attributes, and is not reversable.
   */
  clear(): void {
    this.clearAttributes()
    this.clearErrors()
    this.clearState()
  }

  // Fetch Support

  /**
   * Called before a fetch request is made.
   *
   * @returns {boolean|undefined} `false` if the request should not be made.
   */
  onFetch(): Promise<RequestOperation> {
    return new Promise((resolve): void => {
      // Don't fetch if already fetching. This prevents accidental requests
      // that sometimes occur as a result of a double-click.
      if (this.loading) {
        return resolve(HyBase.REQUEST_SKIP)
      }
      // Set flags
      this.setFlag('loading', true)
      return resolve(HyBase.REQUEST_CONTINUE)
    })
  }

  /**
   * Called when a fetch request was successful.
   */
  onFetchSuccess(response: any): void {
    const attributes: any = response.data.value
    // A fetch request must receive *some* data in return.
    if (isEmpty(attributes)) {
      throw this.createResponseError("No data in fetch response", response);
    }
    // Save data to model
    this.assign(attributes)
    // Set flags
    this.setFlag('loading', false)
    this.setFlag('fatal', false)
    // Send events
    // this.emit('fetch', {error: null});
  }

  /**
   * Called when a fetch request failed.
   *
   * @param {Error}  error
   */
  onFetchFailure(_error: any): void {
    // Set flags
    this.setFlag('loading', false)
    this.setFlag('fatal', true)
    // Send events
    // this.emit('fetch', { error });
  }

  // Save Support

  /**
  * Updates the model data with data returned from the server.
  *
  * @param {Object} response
  */
  update(data: Record<string, any>): void {
    // No content means we don't want to update the model at all.
    // The attributes that we passed in the request should now be considered
    // the source of truth, so we should update the reference attributes here.
    if (!data || (isObjectLike(data) && isEmpty(data))) {
      // Copy data to reference
      this.sync()
      // A plain object implies that we want to update the model data.
      // It's not a requirement to respond with a complete dataset,
      // eg. a response to a patch request might return partial data.
    } else if (isPlainObject(data)) {
      this.assign(defaults({}, data, this._values))
    } else {
      // There is some data, but it's not an object, so we can assume that the
      // response only returned an identifier for this model.
      const identifier: any = this.parseIdentifier(data)
      // It's possible that the response didn't actually return a valid
      // identifier, so before we try to use it we should make sure that
      // we're not accidentially assigning the wrong data as identifiers.
      if (this.isValidIdentifier(identifier)) {
        // The current identifier of this model.
        let current: string = this.identifier()
        // If an identifier already exists on this model and the returned
        // identifier is not the same, this almost definitely indicates
        // an unexpected state. The default is to protect against this
        // and fail hard, but this might not always be what we want.
        if (current && identifier !== current) {
          if (!this.shouldAllowIdentifierOverwrite()) {
            throw new Error(`Not allowed to overwrite model identifier`)
          }
        }
        // Update the identifier and sync the saved data.
        this.set(this.getOption('identifier'), identifier)
        this.sync()
      } else {
        throw new Error(
          'Expected an empty response, object, or valid identifier'
        )
      }
    }
  }

  /**
  * Called before a save request is made.
  *
  * @returns {boolean} `false` if the request should not be made.
  */
  onSave(): Promise<RequestOperation> {
    // Send event
    // this.emit('save', { error: null })
    // Return promise that evaluates pre-flight result
    return new Promise((resolve, reject): void => {
      // Don't save if we're already busy saving this model.
      // This prevents things like accidental double-clicks.
      if (this.saving) {
        return resolve(HyBase.REQUEST_SKIP)
      }
      // Don't save if no data has changed, but consider it a success.
      if (!this.getOption('saveUnchanged') && !this.changed()) {
        return resolve(HyBase.REQUEST_REDUNDANT)
      }
      // Set flags
      this.setFlag('saving', true)
      // Mutate attribute before we save if required to do so.
      // if (this.getOption('mutateBeforeSave')) {
      //   this.mutate()
      // }
      this.validate().then((errors: any): void => {
        if (isEmpty(errors)) {
          return resolve(HyBase.REQUEST_CONTINUE)
        }
        // Set flags
        this.setFlag('saving', false)
        return reject(this.createValidationError(this.errors))
      })
    })
  }

  /**
   * Called when a save request was successful.
   *
   * @param {Object|null} response
   */
  onSaveSuccess(response: any): void {
    // Clear errors because the request was successful.
    this.clearErrors()
    // Assign response values to model
    if (response) {
      // Update this model with the data that was returned in the response.
      this.assign(response.data.value)
    }
    // Set flags
    this.setFlag('saving', false)
    this.setFlag('false', false)
    // Automatically add to all registered collections.
    this.addToAllCollections()
    // Send events
    // this.emit('save.success', { error: null })
    // if (action) {
    //   this.emit(action, { error: null })
    // }
  }

  /**
   * Called when a save request resulted in a validation error.
   *
   * @param {Object} errors
   */
  onSaveValidationFailure(errors: any): void {
    // Add errors to model
    this.setErrors(errors.data as Record<string, any>)
    // Set flags
    this.setFlag('fatal', false)
    this.setFlag('saving', false)
  }

  /**
   * Called when a save request resulted in an unexpected error,
   * eg. an internal server error (500)
   *
   * @param {Error}  error
   * @param {Object} response
   */
  onFatalSaveFailure(_error: any): void {
    this.clearErrors()
    // Set flags
    this.setFlag('fatal', true)
    this.setFlag('saving', false)
  }

  /**
   * Called when a save request resulted in a general error.
   *
   * @param {Error}  error
   * @param {Object} response
   */
  onSaveFailure(error: any): void {
    const errorCode = error.statusCode
    if (errorCode === 422) {
      // Handle back-end validation problems
      this.onSaveValidationFailure(error)
    } else {
      // Handle front-end validation problems
      this.onFatalSaveFailure(error)
    }
    // Send events
    // this.emit('save.failure', { error })
  }

  // Delete Support

  /**
   * Called before a delete request is made.
   *
   * @returns {boolean} `false` if the request should not be made.
   */
  onDelete(): Promise<RequestOperation> {
    return new Promise((resolve): void => {
      // Don't delete if we're already busy deleting this model.
      // This prevents things like accidental double-clicks.
      if (this.deleting) {
        return resolve(HyBase.REQUEST_SKIP)
      }
      // Set flags
      this.setFlag('deleting', true)  
      return resolve(HyBase.REQUEST_CONTINUE)
    })
  }

  /**
   * Called when a delete request was successful.
   */
  onDeleteSuccess(_response: any): void {
    this.clear()
    this.removeFromAllCollections()
    // Set flags
    this.setFlag('deleting', false)
    this.setFlag('fatal', false)
    // Send events
    // this.emit('delete', { error: null })
  }

  /**
   * Called when a delete request resulted in a general error.
   *
   * @param {Error}  error
   */
  onDeleteFailure(_error: any): void {
    // Set flags
    this.setFlag('deleting', false)
    this.setFlag('fatal', true)
    // Send events
    // this.emit('delete', { error })
  }

  // -----------------
  // Custom Actions ==
  // -----------------

  action(name: string, data: any = null): Promise<any> {
    // Get action details
    const action: Action = this.getAction(name)
    // Build the request
    const url = `${this.getBase(action)}/${this.getURL(action)}`
    const config = {
      method: (action.method || 'GET') as ('GET' | 'POST' | 'PUT' | 'DELETE'),
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' as 'include',
      body: data
    }
    // Execute the request
    return useFetch(url, config)
  }

  // ------------------------
  // Collection Management ==
  // ------------------------

  /**
   * Registers a collection on this model. When this model is created it will
   * automatically be added to the collection. Similarly, when this model is
   * delete it will be remove from the collection. Registering the same
   * collection more than once has no effect.
   *
   * @param {HyCollection} collection
   */
  registerCollection(collection: HyCollection | HyCollection[]): void {
    if (isArray(collection)) {
      each(collection, this.registerCollection)
      return
    }
    if (isNil(collection) || isUndefined(collection._uid)) {
      throw new Error('Collection is not valid')
    }
    this._collections[collection._uid] = collection
  }

  /**
   * Removes a collection from this model's collection registry, removing all
   * effects that would occur when creating or deleting this model.
   *
   * Unregistering a collection that isn't registered has no effect.
   *
   * @param {Collection} collection
   */
  unregisterCollection(collection: HyCollection): void {
    if (isArray(collection)) {
      each(collection, this.unregisterCollection)
      return
    }
    if (isNil(collection) || isUndefined(collection._uid)) {
      throw new Error('Collection is not valid')
    }
    // Vue.delete(this._collections, collection._uid)
    delete this._collections[collection._uid]
  }

  /**
   * Adds this model to all registered collections.
   */
  addToAllCollections(): void {
    each(this._collections, (collection): void => {
      collection.add(this)
    })
  }

  /**
   * Removes this model from all registered collections.
   */
  removeFromAllCollections(): void {
    each(this._collections, (collection): void => {
      collection.remove(this)
    })
  }
}

export default HyModel

interface ModelOptions extends Options {
  [key: string]: any

  // methods?: {
  //     [key: string]: HttpMethods;
  // };

  /**
   * The attribute that should be used to uniquely identify this model.
   */
  identifier?: string

  /**
   * The base REST url for the given model
   */
  remote?: string

  /**
   * Whether this model should allow an existing identifier to be
   * overwritten on update.
   */
  // overwriteIdentifier?: boolean;

  /**
   * Route parameter matching pattern.
   */
  // routeParameterPattern?: RegExp;

  /**
   * Whether this model should perform a "patch" on update,
   * which will only send changed attributes in the request.
   */
  patch?: boolean;

  /**
   * Whether this model should save even if no attributes have changed
   * since the last time they were synced. If set to `false` and no
   * changes have been made, the request will be considered a success.
   */
  saveUnchanged?: boolean;

  /**
   * Whether this model should only use the first validation error it
   * receives, rather than an array of errors.
   */
  // useFirstErrorOnly?: boolean;

  /**
   * Whether this model should validate an attribute that has changed.
   * This would only affect the errors of the changed attribute and
   * will only be applied if the value is not a blank string.
   */
  // validateOnChange?: boolean;

  /**
   * Whether this model should be validated before it is saved. This
   * will cause the request to fail if validation does not pass. This
   * is useful when you only want to validate on demand.
   */
  // validateOnSave?: boolean;

  /**
   * Whether this model should validate models and collections within
   * its attribute tree. The result is implicit recursion as each of
   * those instances will also validate their trees, etc.
   */
  // validateRecursively?: boolean;

  /**
   * Whether this model should mutate a property as it is changed,
   * before it is set. This is a rare requirement because you usually
   * don't  want to mutate something that you are busy editing.
   */
  // mutateOnChange?: boolean;

  /**
   * Whether this model should mutate all attributes before they are
   * synced to the "saved" state. This would include construction,
   * on fetch, on save, and on assign.
   */
  // mutateBeforeSync?: boolean;

  /**
   * Whether this model should use mutated values for the attributes
   * in "save" request. This will not mutate the active state.
   */
  // mutateBeforeSave?: boolean;
}

export type ValidationTask = true | string | Promise<ValidationResult>
export type ValidationResult =
  | true
  | string
  | AttributesValidationErrors
  | (string | AttributesValidationErrors)[]
export type ValidationResultError = string | AttributesValidationErrors
export type ValidationResultErrorFinalResult =
  | ValidationResultError
  | ValidationResultError[]

export interface AttributesValidationErrors {
  [key: string]: ValidationResultErrorFinalResult
}
